import './style.css';
import { newGameState, type GameState } from './sim/state';
import { createRenderer, resizeRenderer, GAME_W, GAME_H } from './render/canvas';
import { drawScene, type HudMode, type WeaponPickerState } from './render/draw';
import { newAimState, attachControls, aimToRadians, aimToPowerFrac, newWeaponPickerInput, type AimState } from './input/controls';
import { startLoop } from './loop';
import { updateParticles, spawnExplosion, getWeaponPalette } from './render/particles';
import { triggerShake } from './render/shake';
import { triggerLaunchFx, resetLaunchFx } from './render/launch-fx';
import { placeTanks } from './sim/placement';
import { WEAPONS_BY_ID } from './sim/weapons';
import { newMenuState, drawTitle, drawPauseMenu, drawMatchOver, drawShareLink, drawCodeWait, drawLobby, drawShop, hitTestMenu, CASH_TIERS, type ShareLinkInfo, type CodeWaitInfo, type LobbyInfo, type LobbySlot, type ShopView } from './ui/menu';
import { createMatch, getMatch, updateMatch, joinMatch, startMatch, kickMatch, isApiError } from './online/code-client';
import { encodeMatch, type MatchAction } from './online/match-link';
import { sfxLaunch, sfxExplosion, sfxTurnChange, sfxMatchWin, sfxClick, sfxTankDestroyed, ensureAudio, toggleMuted, isMuted } from './audio/sfx';
import { planAIMove, nextAILevel } from './sim/ai';
import { newOnlineSession, startNewOnlineMatch, loadOnlineMatch, replayActions, recordAction, currentShareUrl, nextPlayerLabel } from './online/online';
import { decodeMatch, readMatchTokenFromUrl } from './online/match-link';
import { WEAPONS } from './sim/weapons';
import { pickDeathQuote, type DeathBanner } from './ui/death-quotes';
import { STARTER_INVENTORY, applyRoundEarnings, shopCatalog, buyOne, sellOne, aiAutoBuy } from './sim/economics';

const screen = document.getElementById('screen') as HTMLCanvasElement;
const renderer = createRenderer(screen);
resizeRenderer(renderer);
window.addEventListener('resize', () => resizeRenderer(renderer));
window.addEventListener('orientationchange', () => resizeRenderer(renderer));

// Support ?seed=N for deterministic testing; default to Date.now()
const url = new URL(window.location.href);
const seedParam = url.searchParams.get('seed');
const initialSeed = seedParam ? parseInt(seedParam, 10) | 0 : (Date.now() & 0xffffffff);

// Menu state drives screen routing
const menu = newMenuState();

// Game state — mutable so "New Game" can rebuild it
let state: GameState = newGameState(initialSeed, menu.playerCount);
let aimStates: AimState[] = state.tanks.map(() => newAimState());

/** Match-start loadout: classic SE starter kit + starting cash from menu config. */
function giveMatchStartKit(s: GameState, startingCash: number): void {
  for (const tank of s.tanks) {
    tank.inventory = { ...STARTER_INVENTORY };
    tank.cash = startingCash;
    tank.deathOrder = 0;
  }
}
/** Between-round reset: preserve inventory and cash, just heal + resurrect. */
function restockFreeRounds(s: GameState): void {
  for (const tank of s.tanks) {
    // Guarantee at least the free weapon is present
    if (!(tank.inventory['baby-missile'] >= 0)) {
      tank.inventory['baby-missile'] = Infinity;
    }
  }
}
giveMatchStartKit(state, menu.startingCash);

// Weapon picker state (shared across turns)
const pickerInput = newWeaponPickerInput();
const pickerState: WeaponPickerState = { open: false, scroll: 0 };

// Wire controls using a getter so it always targets the current player's aim.
// Gear button → open pause menu.
attachControls(
  screen,
  () => aimStates[state.turn.currentPlayer],
  () => { if (menu.screen === 'playing') menu.screen = 'paused'; },
  pickerInput,
  () => state.tanks[state.turn.currentPlayer],
);

// UI state (not sim)
let showingTurnBanner = true;  // show banner for first player too
let lastPlayer = state.turn.currentPlayer;
let roundOverTapReady = false;
const hudMode: HudMode = 'full';

/** Rebuild game state for a fresh match using current menu setup. */
function startNewGame(): void {
  const seed = (Date.now() & 0xffffffff);
  state = newGameState(seed, menu.playerCount);
  giveMatchStartKit(state, menu.startingCash);
  // Apply user-set names
  for (let i = 0; i < state.tanks.length; i++) {
    state.tanks[i].name = menu.names[i];
  }
  aimStates = state.tanks.map(() => newAimState());
  showingTurnBanner = true;
  lastPlayer = 0;
  pickerInput.open = false;
  pickerState.open = false;
  roundOverTapReady = false;
  // Reset match scoring
  menu.wins = new Array(menu.playerCount).fill(0);
  menu.matchWinner = -1;
  lastRoundPhase = 'aiming';
  aliveLastTick = state.tanks.map(() => true);
  resetLaunchFx();
  menu.screen = 'playing';
}

// Track round-over transitions so we award wins exactly once per round
let lastRoundPhase: string = 'aiming';

// Track per-tank alive state across ticks for sfxTankDestroyed
let aliveLastTick: boolean[] = [];

// Most recent death banner (snarky quote). Null when no banner visible.
let deathBanner: DeathBanner | null = null;
const DEATH_BANNER_MS = 2500;

// Shop screen state: who's currently shopping, their scroll offset, and the
// pending queue of remaining shoppers. Earnings are stored for display on the
// scoreboard before the shop opens.
interface ShopSession {
  queue: number[];          // tank indices still to shop (in player order)
  current: number;          // current tank index
  scroll: number;           // catalog scroll offset
  humanPlayers: Set<number>;// tanks that actually get to interact
}
let shopSession: ShopSession | null = null;
let lastRoundEarnings: number[] | null = null; // shown on scoreboard

// AI thinking delay (frames) so player can watch what the bot is doing
let aiThinkFrames = 0;
let aiPlannedThisTurn = false;

// --- Online (share-link multiplayer) session state ---
const online = newOnlineSession();
let shareLinkInfo: ShareLinkInfo = { url: '', nextPlayerName: '', isMatchOver: false };
let codeWaitInfo: CodeWaitInfo = { code: '', status: 'hosting' };
// Track who fired this turn so we know who the "share to next" prompt is for.
// Set when a fire action is recorded; cleared when overlay is shown.
let pendingShareForPlayer: number | null = null;
// Polling timer for code-mode lobby
let codePollTimer: number | null = null;
// Current lobby display state (null when not in lobby)
let lobbyInfo: LobbyInfo | null = null;
// Previous seat count seen by poller (for "Player N joined!" toast detection)
let lastSeatsSeen = 0;

// --- localStorage helpers for recent codes + display name ---
const LS_LAST_CUSTOM = 'scorch.lastCustomCode';
const LS_LAST_JOINED = 'scorch.lastJoinedCode';
const LS_DISPLAY_NAME = 'scorch.displayName';
function loadLastCustomCode(): string {
  try { return localStorage.getItem(LS_LAST_CUSTOM) ?? ''; } catch { return ''; }
}
function saveLastCustomCode(code: string): void {
  try { localStorage.setItem(LS_LAST_CUSTOM, code); } catch { /* quota/disabled */ }
}
function loadLastJoinedCode(): string {
  try { return localStorage.getItem(LS_LAST_JOINED) ?? ''; } catch { return ''; }
}
function saveLastJoinedCode(code: string): void {
  try { localStorage.setItem(LS_LAST_JOINED, code); } catch { /* quota/disabled */ }
}
function loadDisplayName(): string {
  try { return localStorage.getItem(LS_DISPLAY_NAME) ?? ''; } catch { return ''; }
}
function saveDisplayName(name: string): void {
  try { localStorage.setItem(LS_DISPLAY_NAME, name); } catch { /* quota/disabled */ }
}
// Per-code hostToken persistence lets a host reclaim their own lobby after
// a page refresh. Keyed by code so multiple lobbies don't collide.
const LS_HOST_TOKEN_PREFIX = 'scorch.host.';
function loadHostToken(code: string): string {
  try { return localStorage.getItem(LS_HOST_TOKEN_PREFIX + code) ?? ''; } catch { return ''; }
}
function saveHostToken(code: string, token: string): void {
  try { localStorage.setItem(LS_HOST_TOKEN_PREFIX + code, token); } catch { /* quota/disabled */ }
}

/**
 * Build the slot list for the lobby UI from the server's slotOccupied array.
 * `canKick` is set on remote (non-me) occupied slots when the viewer is the host.
 */
function buildSlotsFromOccupied(
  slotOccupied: boolean[],
  maxSeats: number,
  mySlot: number,
  names: string[],
  isHost: boolean,
): LobbySlot[] {
  const slots: LobbySlot[] = [];
  for (let i = 0; i < maxSeats; i++) {
    const occupied = !!slotOccupied[i];
    if (!occupied) {
      slots.push({ name: '', kind: 'empty' });
    } else {
      slots.push({
        name: names[i] ?? `Player ${i + 1}`,
        kind: i === mySlot ? 'local' : 'remote',
        canKick: isHost && i !== mySlot,
      });
    }
  }
  return slots;
}

/** Contiguous-fill slotOccupied for views that only know seatsTaken (initial placeholder). */
function fakeOccupied(seatsTaken: number, maxSeats: number): boolean[] {
  const out = new Array<boolean>(maxSeats).fill(false);
  for (let i = 0; i < Math.min(seatsTaken, maxSeats); i++) out[i] = true;
  return out;
}

// --- Code-based lobby ---

function stopCodePolling(): void {
  if (codePollTimer !== null) {
    clearInterval(codePollTimer);
    codePollTimer = null;
  }
}

function startCodePolling(): void {
  stopCodePolling();
  console.log('[code] startCodePolling');
  codePollTimer = window.setInterval(pollCodeMatch, 1000);
}

async function pollCodeMatch(): Promise<void> {
  if (!online.codeMode) { stopCodePolling(); return; }
  const { serverVersion } = online.codeMode;
  const r = await getMatch(online.codeMode.code);
  if (isApiError(r)) {
    console.warn('[code] poll api error', r);
    return;
  }
  if (r.version <= serverVersion) return; // no change
  console.log(`[code] poll: new version ${r.version} (was ${serverVersion}) status=${r.status} seats=${r.seatsTaken}/${r.maxSeats}`);
  const md = decodeMatch(r.token);
  if (!md) {
    console.warn('[code] poll: bad match data');
    return;
  }

  const wasLobby = menu.screen === 'lobby';

  // --- LOBBY branch: update slots, toast new joiners, wait for host START ---
  if (r.status === 'lobby') {
    // Sync match data (host may have tweaked names in lobby — keep fresh).
    online.matchData = md;
    // Fold joiner-typed names into matchData so the token built at START has
    // the right names and opponents see e.g. "Dave" instead of "Player 2".
    if (r.joinerNames) {
      for (let i = 0; i < r.joinerNames.length && i < online.matchData.names.length; i++) {
        const nm = r.joinerNames[i];
        if (nm) online.matchData.names[i] = nm;
      }
    }
    online.codeMode.serverVersion = r.version;

    const occ = r.slotOccupied ?? fakeOccupied(r.seatsTaken, r.maxSeats);
    const mySlot = online.codeMode.myPlayer;
    const isHost = mySlot === 0;

    // --- KICK detection (joiner only): server dropped me from the lobby ---
    if (!isHost && mySlot > 0 && occ.length > mySlot && !occ[mySlot]) {
      console.log('[code] we were kicked from the lobby');
      stopCodePolling();
      online.codeMode = null;
      online.active = false;
      lobbyInfo = null;
      lastSeatsSeen = 0;
      menu.screen = 'title';
      window.alert('You were kicked from the lobby.');
      return;
    }

    if (r.seatsTaken > lastSeatsSeen) {
      // Most-recent join is the highest occupied index we hadn't counted yet.
      let joinedSlot = -1;
      for (let i = occ.length - 1; i >= 0; i--) {
        if (occ[i]) { joinedSlot = i; break; }
      }
      if (joinedSlot >= 0) {
        const name = online.matchData.names[joinedSlot] ?? `Player ${joinedSlot + 1}`;
        flashToast(`${name} joined!`);
      }
    }
    lastSeatsSeen = r.seatsTaken;

    if (lobbyInfo && online.codeMode) {
      lobbyInfo.slots = buildSlotsFromOccupied(occ, r.maxSeats, mySlot, online.matchData.names, lobbyInfo.isHost);
      lobbyInfo.statusMessage = lobbyInfo.isHost
        ? (r.seatsTaken > 1 ? 'Ready when you are \u2014 tap START GAME.' : 'Waiting for players to join\u2026')
        : 'Waiting for host to start the game\u2026';
      lobbyInfo.errorMessage = undefined;
    }
    return;
  }

  // --- PLAYING branch: either we already transitioned here or host just started ---
  // Sync match data and version.
  const oldCount = online.matchData.actions.length;
  const newActions = md.actions.slice(oldCount);
  online.matchData = md;
  online.codeMode.serverVersion = r.version;

  // Joiner transition: was in lobby, server flipped to playing — move to game canvas.
  if (wasLobby) {
    console.log('[code] host started the game \u2014 transitioning to playing');
    buildOnlineState();
    lobbyInfo = null;
    menu.screen = 'playing';
    showingTurnBanner = true;
    return;
  }

  if (newActions.length === 0) {
    // Pure version bump. Nothing to replay.
    console.log('[code] version bump, no actions to apply');
    return;
  }

  if (newActions.length === 1) {
    // Feed as pendingFire so the live game loop animates the shot in real time.
    console.log('[code] applying 1 new action live');
    applyActionLive(newActions[0]);
  } else {
    // Desync / missed several polls — fall back to full rebuild.
    console.log(`[code] applying ${newActions.length} actions (catch-up rebuild)`);
    buildOnlineState();
  }
}

/** Feed a remote action into the running sim as pendingFire so the next
 *  tick consumes it and animates the shot live. */
function applyActionLive(action: MatchAction): void {
  const cp = state.turn.currentPlayer;
  const tank = state.tanks[cp];
  const weapon = WEAPONS[action.weaponIdx];
  if (weapon && tank) {
    if (!weapon.isFree) {
      const qty = tank.inventory[weapon.id] ?? 0;
      if (qty > 0) tank.inventory[weapon.id] = qty - 1;
    }
    tank.selectedWeapon = weapon.id;
  }
  state.pendingFire = {
    angle: aimToRadians(action.angleDeg),
    power: aimToPowerFrac(action.power),
  };
}

async function startCodeHost(): Promise<void> {
  // Optional: ask for a custom code (persistent, 30-day TTL). Blank = random.
  const lastCustom = loadLastCustomCode();
  const entered = window.prompt(
    '(Optional) Custom code to save this lobby for future games (4\u201312 chars, letters & numbers).\nLeave blank for a random 5-letter code.',
    lastCustom,
  );
  // null = user cancelled the dialog entirely — bail.
  if (entered === null) return;
  let customCode: string | undefined;
  if (entered.trim()) {
    const code = entered.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,12}$/.test(code)) {
      window.alert('Code must be 4\u201312 characters using letters and numbers only.');
      return;
    }
    customCode = code;
  }

  // Optional host password — ONLY prompted for custom codes. Blank = skip.
  // Used both to SET a password on fresh creates and to PROVE ownership on
  // reclaim attempts when the stored hostToken is gone.
  let hostPassword: string | undefined;
  if (customCode) {
    const pwPrompt = window.prompt(
      '(Optional) Host password for "' + customCode + '" \u2014 lets you reclaim this code from another device or after clearing browser data. Blank = none.',
      '',
    );
    if (pwPrompt === null) return; // user cancelled
    if (pwPrompt.trim()) hostPassword = pwPrompt.trim().slice(0, 64);
  }

  // Ask the host for their display name — same flow as joiners, same LS key.
  const savedName = loadDisplayName();
  const hostNameDefault = savedName
    || ((menu.names[0] && menu.names[0] !== 'Player 1') ? menu.names[0] : 'Host');
  const nameEntered = window.prompt('Your display name:', hostNameDefault);
  if (nameEntered === null) return;
  const hostName = (nameEntered.trim() || 'Host').slice(0, 14);
  saveDisplayName(hostName);
  menu.names[0] = hostName;

  const maxSeats = Math.max(2, Math.min(4, menu.playerCount));
  const seed = (Date.now() & 0xffffffff);
  const namesForMatch = menu.names.slice(0, maxSeats);
  namesForMatch[0] = hostName;
  startNewOnlineMatch(online, {
    seed,
    matchTarget: menu.matchTarget,
    playerCount: maxSeats,
    names: namesForMatch,
  });
  buildOnlineState();
  // Placeholder codeMode so badge/gating works even before network returns.
  online.codeMode = { code: customCode ?? '-----', myPlayer: 0, serverVersion: 0 };
  // Show lobby screen immediately with placeholder code.
  lobbyInfo = {
    code: customCode ?? '-----',
    slots: buildSlotsFromOccupied(fakeOccupied(1, maxSeats), maxSeats, 0, menu.names, true),
    isHost: true,
    statusMessage: 'Creating lobby\u2026',
  };
  menu.screen = 'lobby';
  lastSeatsSeen = 1;
  const token = encodeMatch(online.matchData);
  // If this host created this customCode before, pass the stored hostToken so
  // the server lets us reclaim an abandoned or in-progress record.
  const reclaimHostToken = customCode ? loadHostToken(customCode) || undefined : undefined;
  const r = await createMatch(token, maxSeats, { customCode, reclaimHostToken, hostPassword });
  if (isApiError(r)) {
    if (lobbyInfo) {
      if (r.error === 'code-taken') {
        lobbyInfo.errorMessage = `"${customCode}" is in use by another host. Try a different code, or (if you set one) enter your host password.`;
      } else if (r.error === 'bad-password') {
        lobbyInfo.errorMessage = `Wrong host password for "${customCode}". Tap CANCEL and try again.`;
      } else {
        lobbyInfo.errorMessage = `Error: ${r.message ?? r.error}`;
      }
    }
    online.codeMode = null;
    online.active = false;
    return;
  }
  online.codeMode.code = r.code;
  online.codeMode.serverVersion = r.version;
  online.codeMode.hostToken = r.hostToken;
  // Persist the (possibly-rotated) token per-code so a refresh can reclaim
  // this lobby seamlessly next time.
  saveHostToken(r.code, r.hostToken);
  if (lobbyInfo) {
    lobbyInfo.code = r.code;
    lobbyInfo.slots = buildSlotsFromOccupied(fakeOccupied(r.seatsTaken, r.maxSeats), r.maxSeats, 0, menu.names, true);
    const reclaimMsg = r.reclaimMethod === 'password'
      ? 'Reclaimed with password \u2014 opponents can rejoin now.'
      : 'Reclaimed your previous lobby \u2014 opponents can rejoin now.';
    lobbyInfo.statusMessage = r.reclaimed
      ? reclaimMsg
      : r.persistent
        ? 'Code saved \u2014 reuse it next time you play together.'
        : 'Waiting for players to join\u2026';
    lobbyInfo.errorMessage = undefined;
  }
  lastSeatsSeen = r.seatsTaken;
  if (customCode) saveLastCustomCode(customCode);
  startCodePolling();
}

async function startCodeJoin(): Promise<void> {
  const lastCode = loadLastJoinedCode();
  const entered = window.prompt('Enter game code (4\u201312 characters):', lastCode);
  if (!entered) return;
  const code = entered.trim().toUpperCase();
  if (!/^[A-Z0-9]{4,12}$/.test(code)) {
    window.alert('Code must be 4\u201312 characters using letters and numbers only.');
    return;
  }
  const savedName = loadDisplayName();
  const nameDefault = savedName
    || ((menu.names[1] && menu.names[1] !== 'Player 2') ? menu.names[1] : 'Player');
  const nameEntered = window.prompt('Your display name:', nameDefault);
  if (nameEntered === null) return;
  const joinerName = (nameEntered.trim() || 'Player').slice(0, 14);
  saveDisplayName(joinerName);

  flashToast('Joining...');
  const r = await joinMatch(code, joinerName);
  if (isApiError(r)) {
    if (r.error === 'game-already-started') {
      window.alert('Game has started, please host a new game.');
    } else if (r.error === 'lobby-full') {
      window.alert('That lobby is full.');
    } else if (r.error === 'not-found') {
      window.alert('No match with that code. Check the letters and try again.');
    } else {
      window.alert(`Error: ${r.message ?? r.error}`);
    }
    return;
  }
  const md = decodeMatch(r.token);
  if (!md) {
    flashToast('Invalid match data');
    return;
  }
  loadOnlineMatch(online, md);
  // Overwrite our own slot's name locally with what we just typed, so the UI
  // reflects it instantly without waiting for the next poll round-trip.
  if (online.matchData.names[r.slot] !== undefined) {
    online.matchData.names[r.slot] = joinerName;
  }
  // Fold any other joiner-typed names the server already has.
  if (r.joinerNames) {
    for (let i = 0; i < r.joinerNames.length && i < online.matchData.names.length; i++) {
      const nm = r.joinerNames[i];
      if (nm) online.matchData.names[i] = nm;
    }
  }
  online.codeMode = { code, myPlayer: r.slot, serverVersion: r.version };
  buildOnlineState();
  // Show lobby screen while host finalizes + starts the game.
  const occ = r.slotOccupied ?? fakeOccupied(r.seatsTaken, r.maxSeats);
  lobbyInfo = {
    code,
    slots: buildSlotsFromOccupied(occ, r.maxSeats, r.slot, online.matchData.names, false),
    isHost: false,
    statusMessage: 'Waiting for host to start the game\u2026',
  };
  lastSeatsSeen = r.seatsTaken;
  menu.screen = 'lobby';
  saveLastJoinedCode(code);
  startCodePolling();
}

/** Host taps START GAME in the lobby — flip server status to 'playing' and transition. */
async function startLobbyGame(): Promise<void> {
  if (!online.codeMode || !lobbyInfo) return;
  const { code, serverVersion } = online.codeMode;
  // Trim matchData to only the seats that actually joined.
  const actualPlayers = lobbyInfo.slots.filter(s => s.kind !== 'empty').length;
  if (actualPlayers < 2) {
    if (lobbyInfo) lobbyInfo.errorMessage = 'Need at least one other player to start.';
    return;
  }
  online.matchData.playerCount = actualPlayers;
  online.matchData.names = online.matchData.names.slice(0, actualPlayers);
  menu.playerCount = actualPlayers;
  const token = encodeMatch(online.matchData);
  const r = await startMatch(code, serverVersion, token, online.codeMode.hostToken);
  if (isApiError(r)) {
    if (r.error === 'version-conflict' && typeof r.version === 'number') {
      online.codeMode.serverVersion = r.version;
      pollCodeMatch();
      return;
    }
    if (lobbyInfo) lobbyInfo.errorMessage = `Error: ${r.message ?? r.error}`;
    return;
  }
  online.codeMode.serverVersion = r.version;
  // Rebuild sim with trimmed match data so tanks match actual players.
  buildOnlineState();
  lobbyInfo = null;
  menu.screen = 'playing';
  showingTurnBanner = true;
}

/** Host clicks × next to a joiner — confirm and tell the server to drop them. */
async function performLobbyKick(slot: number): Promise<void> {
  if (!online.codeMode || !online.codeMode.hostToken || !lobbyInfo) return;
  if (slot <= 0 || slot >= lobbyInfo.slots.length) return;
  const target = lobbyInfo.slots[slot];
  if (!target || target.kind !== 'remote') return;
  const name = target.name || `Player ${slot + 1}`;
  if (!window.confirm(`Kick ${name} from the lobby?`)) return;
  const { code, hostToken } = online.codeMode;
  const r = await kickMatch(code, slot, hostToken);
  if (isApiError(r)) {
    if (lobbyInfo) lobbyInfo.errorMessage = `Kick failed: ${r.message ?? r.error}`;
    return;
  }
  online.codeMode.serverVersion = r.version;
  // Clear the slot locally so the UI updates immediately; poll will confirm.
  if (online.matchData.names[slot] !== undefined) {
    online.matchData.names[slot] = `Player ${slot + 1}`;
  }
  if (lobbyInfo) {
    lobbyInfo.slots[slot] = { name: '', kind: 'empty' };
    lobbyInfo.errorMessage = undefined;
    flashToast(`${name} kicked`);
  }
  // Seat count went down — let the next "joined!" toast fire for genuine new joiners.
  lastSeatsSeen = Math.max(0, lastSeatsSeen - 1);
}

async function pushCodeUpdate(): Promise<void> {
  if (!online.codeMode) return;
  const { code, serverVersion } = online.codeMode;
  const token = encodeMatch(online.matchData);
  const r = await updateMatch(code, token, serverVersion);
  if (isApiError(r)) {
    if (r.error === 'version-conflict' && typeof r.version === 'number') {
      // Someone moved while we were thinking — refetch.
      online.codeMode.serverVersion = r.version;
      pollCodeMatch();
      return;
    }
    codeWaitInfo = { ...codeWaitInfo, status: 'error', message: r.message ?? r.error };
    return;
  }
  online.codeMode.serverVersion = r.version;
}

async function copyCodeText(): Promise<void> {
  if (!online.codeMode) return;
  try {
    await navigator.clipboard.writeText(online.codeMode.code);
    flashToast('Code copied!');
  } catch {
    window.prompt('Copy this code:', online.codeMode.code);
  }
}

/** Begin a brand-new online match using the current title-screen config. */
function startOnlineMatch(): void {
  const seed = (Date.now() & 0xffffffff);
  startNewOnlineMatch(online, {
    seed,
    matchTarget: menu.matchTarget,
    playerCount: menu.playerCount,
    names: menu.names.slice(0, menu.playerCount),
  });
  buildOnlineState();
  menu.screen = 'playing';
}

/** Build a fresh sim state from the current online.matchData and replay all actions. */
function buildOnlineState(): void {
  state = newGameState(online.matchData.seed, online.matchData.playerCount);
  for (let i = 0; i < state.tanks.length; i++) {
    state.tanks[i].name = online.matchData.names[i] ?? `Player ${i + 1}`;
  }
  giveMatchStartKit(state, menu.startingCash);
  aimStates = state.tanks.map(() => newAimState());
  menu.wins = new Array(online.matchData.playerCount).fill(0);
  menu.matchWinner = -1;
  menu.matchTarget = online.matchData.matchTarget;
  menu.playerCount = online.matchData.playerCount;
  for (let i = 0; i < online.matchData.names.length; i++) {
    menu.names[i] = online.matchData.names[i];
  }
  // Force human-only in online mode
  menu.aiLevels = ['human', 'human', 'human', 'human'];

  // Replay
  online.replaying = true;
  replayActions(state, online.matchData.actions, resetRound, (s) => giveMatchStartKit(s, menu.startingCash));
  online.replaying = false;

  // Re-tally wins from action history (so the scoreboard pips are right).
  // We need to know which round the player won — easiest: track during replay.
  // For now, walk the killLog isn't enough (it resets per round). Skip for v1;
  // wins will simply build up live as we play forward.

  showingTurnBanner = true;
  lastPlayer = state.turn.currentPlayer;
  pickerInput.open = false;
  pickerState.open = false;
  roundOverTapReady = false;
  lastRoundPhase = state.turn.phase;
  aliveLastTick = state.tanks.map(t => t.alive);
  resetLaunchFx();
}

async function copyShareLink(): Promise<void> {
  try {
    await navigator.clipboard.writeText(shareLinkInfo.url);
    flashToast('Link copied!');
  } catch {
    window.prompt('Copy this link:', shareLinkInfo.url);
  }
}

async function shareNativeLink(): Promise<void> {
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (typeof nav.share !== 'function') {
    copyShareLink();
    return;
  }
  try {
    await nav.share({
      title: 'SCORCH! — your turn',
      text: shareLinkInfo.isMatchOver
        ? 'Match complete!'
        : `It's your turn in SCORCH! — ${shareLinkInfo.nextPlayerName}`,
      url: shareLinkInfo.url,
    });
  } catch {
    /* user cancelled — no-op */
  }
}

let toastText = '';
let toastUntil = 0;
function flashToast(msg: string): void {
  toastText = msg;
  toastUntil = performance.now() + 1500;
}

/** Try to boot from a share-link URL token. Returns true if we loaded a match. */
function tryBootFromUrl(): boolean {
  const token = readMatchTokenFromUrl();
  if (!token) return false;
  const m = decodeMatch(token);
  if (!m) {
    window.alert('That game link is invalid or from an incompatible version.');
    return false;
  }
  loadOnlineMatch(online, m);
  buildOnlineState();
  menu.screen = 'playing';
  return true;
}

// Bootstrap audio context on first user gesture (browser autoplay rule)
let audioBootstrapped = false;
function bootstrapAudioOnce(): void {
  if (audioBootstrapped) return;
  audioBootstrapped = true;
  ensureAudio();
}
window.addEventListener('pointerdown', bootstrapAudioOnce, { once: true });
window.addEventListener('keydown', bootstrapAudioOnce, { once: true });

function screenToGame(clientX: number, clientY: number): { gx: number; gy: number } {
  const rect = screen.getBoundingClientRect();
  return {
    gx: ((clientX - rect.left) / rect.width) * GAME_W,
    gy: ((clientY - rect.top) / rect.height) * GAME_H,
  };
}

// Menu tap handler — capture phase so it runs before in-game controls
screen.addEventListener('pointerdown', (e) => {
  if (menu.screen !== 'title' && menu.screen !== 'paused' && menu.screen !== 'match-over' && menu.screen !== 'share-link' && menu.screen !== 'code-wait' && menu.screen !== 'lobby' && menu.screen !== 'shop') return;
  const { gx, gy } = screenToGame(e.clientX, e.clientY);
  const action = hitTestMenu(gx, gy);
  if (!action) {
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
  sfxClick();
  handleMenuAction(action);
  e.preventDefault();
  e.stopImmediatePropagation();
}, true);

function handleMenuAction(action: string): void {
  switch (action) {
    case 'count-dec':
      menu.playerCount = Math.max(2, menu.playerCount - 1);
      break;
    case 'count-inc':
      menu.playerCount = Math.min(4, menu.playerCount + 1);
      break;
    case 'target-dec':
      menu.matchTarget = Math.max(1, menu.matchTarget - 1);
      break;
    case 'target-inc':
      menu.matchTarget = Math.min(5, menu.matchTarget + 1);
      break;
    case 'cash-dec': {
      const i = CASH_TIERS.indexOf(menu.startingCash as typeof CASH_TIERS[number]);
      if (i > 0) menu.startingCash = CASH_TIERS[i - 1];
      break;
    }
    case 'cash-inc': {
      const i = CASH_TIERS.indexOf(menu.startingCash as typeof CASH_TIERS[number]);
      if (i >= 0 && i < CASH_TIERS.length - 1) menu.startingCash = CASH_TIERS[i + 1];
      break;
    }
    case 'start':
    case 'new-game':
    case 'rematch':
      online.active = false;
      startNewGame();
      break;
    case 'start-online':
      startOnlineMatch();
      break;
    case 'code-host':
      startCodeHost();
      break;
    case 'code-join':
      startCodeJoin();
      break;
    case 'code-copy':
      copyCodeText();
      break;
    case 'lobby-start':
      startLobbyGame();
      break;
    case 'share-copy':
      copyShareLink();
      break;
    case 'share-native':
      shareNativeLink();
      break;
    case 'share-continue':
      // Same-device hot-seat continuation: just resume the game.
      online.showShareOverlay = false;
      menu.screen = 'playing';
      showingTurnBanner = true;
      break;
    case 'resume':
      menu.screen = 'playing';
      break;
    case 'title':
      stopCodePolling();
      online.codeMode = null;
      online.active = false;
      lobbyInfo = null;
      lastSeatsSeen = 0;
      menu.screen = 'title';
      break;
    case 'toggle-sound':
      toggleMuted();
      break;
    default:
      if (action.startsWith('edit-name:')) {
        const i = parseInt(action.split(':')[1], 10);
        const entered = window.prompt(`Player ${i + 1} name:`, menu.names[i]);
        if (entered !== null) {
          const trimmed = entered.trim().slice(0, 14);
          if (trimmed) menu.names[i] = trimmed;
        }
      } else if (action.startsWith('cycle-ai:')) {
        const i = parseInt(action.split(':')[1], 10);
        menu.aiLevels[i] = nextAILevel(menu.aiLevels[i] ?? 'human');
      } else if (action.startsWith('lobby-kick:')) {
        const slot = parseInt(action.split(':')[1], 10);
        performLobbyKick(slot);
      } else if (action.startsWith('shop-buy:')) {
        const id = action.slice('shop-buy:'.length);
        if (shopSession) buyOne(state.tanks[shopSession.current], id);
      } else if (action.startsWith('shop-sell:')) {
        const id = action.slice('shop-sell:'.length);
        if (shopSession) sellOne(state.tanks[shopSession.current], id);
      } else if (action === 'shop-scroll-up') {
        if (shopSession) shopSession.scroll = Math.max(0, shopSession.scroll - 1);
      } else if (action === 'shop-scroll-down') {
        if (shopSession) shopSession.scroll += 1;
      } else if (action === 'shop-done') {
        advanceShop();
      } else if (action === 'shop-skip-all') {
        // End the entire shop sequence immediately
        if (shopSession) {
          shopSession.queue = [];
          advanceShop();
        }
      }
      break;
  }
}

// ESC key → pause / back to title
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    if (menu.screen === 'playing') menu.screen = 'paused';
    else if (menu.screen === 'paused') menu.screen = 'playing';
    e.preventDefault();
  }
  // Shop keyboard controls
  if (menu.screen === 'shop' && shopSession) {
    const catalog = shopCatalog();
    const tank = state.tanks[shopSession.current];
    // Track a highlighted row via scroll position — for simplicity the "current"
    // row is the top visible row. Up/Down scrolls; +/- buys/sells that row.
    if (e.key === 'ArrowDown') {
      shopSession.scroll = Math.min(catalog.length - 1, shopSession.scroll + 1);
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      shopSession.scroll = Math.max(0, shopSession.scroll - 1);
      e.preventDefault();
    } else if (e.key === '+' || e.key === '=') {
      const def = catalog[shopSession.scroll];
      if (def) buyOne(tank, def.id);
      e.preventDefault();
    } else if (e.key === '-' || e.key === '_') {
      const def = catalog[shopSession.scroll];
      if (def) sellOne(tank, def.id);
      e.preventDefault();
    } else if (e.key === 'Enter') {
      advanceShop();
      e.preventDefault();
    }
  }
});

// Tap to dismiss turn banner or round-over scoreboard (in-game only)
screen.addEventListener('pointerdown', (e) => {
  if (menu.screen !== 'playing') return;
  if (showingTurnBanner) {
    showingTurnBanner = false;
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
  if (state.turn.phase === 'round-over' && roundOverTapReady) {
    roundOverTapReady = false;
    e.preventDefault();
    e.stopImmediatePropagation();
    // If match is already over, don't open shop; match-over screen handles flow
    if (menu.matchWinner >= 0) {
      resetRound(state);
      return;
    }
    openShop();
    return;
  }
});

/** Start the between-round shopping sequence. AI tanks auto-buy silently. */
function openShop(): void {
  // Queue all tanks in player order. AI ones get auto-bought immediately and
  // don't occupy the shop screen.
  const humanSet = new Set<number>();
  for (let i = 0; i < state.tanks.length; i++) {
    if ((menu.aiLevels[i] ?? 'human') === 'human') humanSet.add(i);
  }
  // AI auto-buys before any human shops so balance feels right
  for (let i = 0; i < state.tanks.length; i++) {
    if (!humanSet.has(i)) aiAutoBuy(state.tanks[i]);
  }
  const queue = [...humanSet];
  if (queue.length === 0) {
    // No humans shopping — skip straight to next round
    resetRound(state);
    return;
  }
  shopSession = {
    queue,
    current: queue[0],
    scroll: 0,
    humanPlayers: humanSet,
  };
  menu.screen = 'shop';
}

/** Advance to next shopper, or end the shop and start the next round. */
function advanceShop(): void {
  if (!shopSession) return;
  shopSession.queue.shift();
  if (shopSession.queue.length === 0) {
    shopSession = null;
    menu.screen = 'playing';
    resetRound(state);
    return;
  }
  shopSession.current = shopSession.queue[0];
  shopSession.scroll = 0;
}

function currentShopView(): ShopView | null {
  if (!shopSession) return null;
  const tank = state.tanks[shopSession.current];
  const catalog = shopCatalog();
  const total = shopSession.humanPlayers.size;
  // "Shopper N of total" — count done = total - remaining + 1
  const remaining = shopSession.queue.length;
  const order = total - remaining + 1;
  return {
    shopperIndex: shopSession.current,
    shopperName: tank.name,
    shopperColor: tank.color,
    shopperCash: tank.cash,
    shopperInventory: tank.inventory,
    catalog,
    scroll: shopSession.scroll,
    roundNumber: state.turn.roundNumber,
    totalShoppers: total,
    shopperOrder: order,
  };
}

function resetRound(s: GameState): void {
  s.seed = (s.seed + 7) | 0; // new seed for variety
  s.terrain.generate(s.seed);
  const positions = placeTanks(s.tanks.length, s.terrain, s.rng);
  for (let i = 0; i < s.tanks.length; i++) {
    s.tanks[i].x = positions[i].x;
    s.tanks[i].y = positions[i].y;
    s.tanks[i].hp = 100;
    s.tanks[i].alive = true;
    s.tanks[i].parachutes = 0;
    s.tanks[i].shields = 0;
    s.tanks[i].fuel = 0;
    s.tanks[i].flashTimer = 0;
    s.tanks[i].selectedWeapon = 'baby-missile';
    s.tanks[i].activeShield = null;
    s.tanks[i].deathOrder = 0;
  }
  restockFreeRounds(s);
  s.turn.phase = 'aiming';
  s.turn.currentPlayer = 0;
  s.turn.roundNumber++;
  s.turn.killLog = [];
  s.turn.deathQueue = [];
  s.projectiles.forEach(p => s.projectiles.release(p));
  s.activeEffects = [];
  s.collapsing = false;
  s.pendingFire = null;
  s.lastHit = null;
  s.world.wind = (s.rng() - 0.5) * 40; // re-roll wind
  lastPlayer = 0;
  showingTurnBanner = true;
  resetLaunchFx();
}

const SIM_DT = 1 / 60;

function blitToScreen(): void {
  renderer.sctx.drawImage(renderer.game, 0, 0, renderer.screen.width, renderer.screen.height);
}

startLoop(
  () => state,
  () => {
    if (menu.screen === 'title') {
      drawTitle(renderer.gctx, menu);
      blitToScreen();
      return;
    }
    if (menu.screen === 'paused') {
      // Game frame already drew itself at last play tick; re-draw for fresh frame,
      // then overlay pause menu on top.
      drawGameFrame();
      drawPauseMenu(renderer.gctx, !isMuted());
      blitToScreen();
      return;
    }
    if (menu.screen === 'match-over') {
      drawGameFrame();
      drawMatchOver(renderer.gctx, menu);
      blitToScreen();
      return;
    }
    if (menu.screen === 'share-link') {
      drawGameFrame();
      drawShareLink(renderer.gctx, shareLinkInfo);
      drawToast();
      blitToScreen();
      return;
    }
    if (menu.screen === 'code-wait') {
      drawCodeWait(renderer.gctx, codeWaitInfo);
      drawToast();
      blitToScreen();
      return;
    }
    if (menu.screen === 'lobby' && lobbyInfo) {
      drawLobby(renderer.gctx, lobbyInfo);
      drawToast();
      blitToScreen();
      return;
    }
    if (menu.screen === 'shop') {
      const sv = currentShopView();
      if (sv) {
        drawShop(renderer.gctx, sv);
      }
      drawToast();
      blitToScreen();
      return;
    }
    // Normal in-game render (drawScene internally blits to screen)
    tickGame();
    drawGameFrame();
    drawCodeBadge();
    drawToast();
  },
  () => menu.screen === 'playing',
);

function drawCodeBadge(): void {
  if (!online.codeMode) return;
  const ctx = renderer.gctx;
  const pending = online.codeMode.serverVersion === 0;
  const code = pending ? '. . . .' : online.codeMode.code;
  const isMyTurn = state.turn.currentPlayer === online.codeMode.myPlayer;
  const subtitle = pending
    ? 'creating lobby\u2026'
    : (isMyTurn ? 'your turn' : 'opponent\u2019s turn');
  ctx.save();
  // Top-right corner badge
  const w = 200;
  const h = 56;
  const x = GAME_W - w - 16;
  const y = 16;
  ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = pending ? '#888' : '#55ff88';
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = pending ? '#cccccc' : '#8fffb8';
  ctx.font = 'bold 22px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code, x + w / 2, y + 20);
  ctx.fillStyle = pending ? '#aaaaaa' : (isMyTurn ? '#aaffaa' : '#cccccc');
  ctx.font = '13px monospace';
  ctx.fillText(subtitle, x + w / 2, y + 42);
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

function drawToast(): void {
  if (!toastText || performance.now() > toastUntil) return;
  const ctx = renderer.gctx;
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(GAME_W / 2 - 100, 20, 200, 36);
  ctx.fillStyle = '#aaffaa';
  ctx.font = 'bold 16px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(toastText, GAME_W / 2, 38);
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

// --- Boot from share-link URL if present ---
if (!tryBootFromUrl()) {
  // Stay on title screen with default state for hot-seat play.
}

function tickGame(): void {
  const s = state;
  const cp = s.turn.currentPlayer;
  const currentAim = aimStates[cp];

  // Sync picker state from input to render
  pickerState.open = pickerInput.open;
  pickerState.scroll = pickerInput.scroll;

  // Close picker on turn change
  if (cp !== lastPlayer) {
    // ONLINE: when the turn flips away from the player who just fired,
    // the action has fully resolved.
    if (online.active && pendingShareForPlayer !== null) {
      const aliveArr = s.tanks.map(t => t.alive);
      const isMatchOver = menu.matchWinner >= 0 || aliveArr.filter(Boolean).length <= 1;
      const firedBy = pendingShareForPlayer;
      pendingShareForPlayer = null;

      if (online.codeMode) {
        // Code-based real-time: push update to server and stay on the game canvas.
        // Both players remain on 'playing' for the whole match; input is gated
        // below (fire only when it's your turn).
        pushCodeUpdate();
        // Fall through to the normal turn-change handling (banner + sfx).
      } else {
        // Share-link mode (legacy): show URL screen.
        shareLinkInfo = {
          url: currentShareUrl(online),
          nextPlayerName: nextPlayerLabel(online, firedBy, aliveArr),
          isMatchOver,
          matchWinnerName: isMatchOver && menu.matchWinner >= 0 ? menu.names[menu.matchWinner] : undefined,
        };
        online.showShareOverlay = true;
        menu.screen = 'share-link';
        lastPlayer = cp;
        return;
      }
    }
    showingTurnBanner = true;
    lastPlayer = cp;
    pickerInput.open = false;
    sfxTurnChange();
    aiPlannedThisTurn = false;
    aiThinkFrames = 0;
  }

  // AI turn handling: auto-dismiss banner after ~1s, then think + fire
  const aiLevel = menu.aiLevels[cp] ?? 'human';
  const isAITurn = aiLevel !== 'human' && s.tanks[cp].alive && s.turn.phase === 'aiming';
  if (isAITurn) {
    aiThinkFrames++;
    // Auto-dismiss the banner after ~60 frames (1s)
    if (showingTurnBanner && aiThinkFrames > 60) {
      showingTurnBanner = false;
    }
    // Plan + commit aim once banner is gone, then fire ~30 frames later
    if (!showingTurnBanner && !aiPlannedThisTurn && !s.collapsing && s.projectiles.activeCount === 0) {
      const plan = planAIMove(s, s.tanks[cp], aiLevel);
      currentAim.angle = plan.angleDeg;
      currentAim.power = plan.power;
      // Only switch weapon if we actually have ammo (AI plan already filters)
      s.tanks[cp].selectedWeapon = plan.weaponId;
      aiPlannedThisTurn = true;
      aiThinkFrames = 0;
    }
    if (aiPlannedThisTurn && aiThinkFrames > 30 && !currentAim.fired) {
      currentAim.fired = true;
    }
  }

  // Enable round-over tap after a brief delay (prevent accidental dismiss)
  if (s.turn.phase === 'round-over') {
    roundOverTapReady = true;
  }

  // Detect round-over transition exactly once to award match points
  if (lastRoundPhase !== 'round-over' && s.turn.phase === 'round-over') {
    const survivors = s.tanks.map((t, i) => ({ t, i })).filter(x => x.t.alive);
    if (survivors.length === 1) {
      const winnerIdx = survivors[0].i;
      if (winnerIdx < menu.wins.length) {
        menu.wins[winnerIdx]++;
        if (menu.wins[winnerIdx] >= menu.matchTarget) {
          menu.matchWinner = winnerIdx;
          menu.screen = 'match-over';
          sfxMatchWin();
        }
      }
    }
    // Draw (0 survivors) awards no points.

    // Award round-end earnings to every tank (even the dead) for display on
    // the scoreboard and spending in the shop.
    lastRoundEarnings = applyRoundEarnings(s.tanks, s.turn.killLog);

    // ONLINE code-mode: when a shot ends the round, `currentPlayer` doesn't
    // change, so the normal cp!==lastPlayer push path never fires. Push here
    // so the opponent's client receives the killing action.
    if (online.active && online.codeMode && pendingShareForPlayer !== null) {
      pendingShareForPlayer = null;
      pushCodeUpdate();
    }
  }
  lastRoundPhase = s.turn.phase;

  // Aim preview line -- always show during aiming phase (even behind banner)
  if (s.turn.phase === 'aiming' && s.tanks[cp].alive) {
    const radians = aimToRadians(currentAim.angle);
    const len = (currentAim.power / 1000) * 120;
    s.aimPreview.visible = true;
    s.aimPreview.x = s.tanks[cp].x + Math.cos(radians) * len;
    s.aimPreview.y = s.tanks[cp].y + Math.sin(radians) * len;
  } else {
    s.aimPreview.visible = false;
  }

  // Block fire input during turn banner. In code mode, also block fire when:
  //   - it isn't our turn (opponent's shot will arrive via poll)
  //   - the lobby is still being created (serverVersion === 0)
  const isMyCodeTurn = !online.codeMode || (
    online.codeMode.serverVersion > 0 &&
    s.turn.currentPlayer === online.codeMode.myPlayer
  );
  if (!showingTurnBanner && s.turn.phase === 'aiming' && isMyCodeTurn) {
    if (currentAim.fired && !s.collapsing && s.projectiles.activeCount === 0) {
      // Check ammo BEFORE firing (don't switch weapon yet — stepSim reads selectedWeapon)
      const tank = s.tanks[cp];
      const wpnDef = WEAPONS_BY_ID[tank.selectedWeapon];
      if (wpnDef && !wpnDef.isFree) {
        const qty = tank.inventory[tank.selectedWeapon] ?? 0;
        if (qty <= 0) {
          tank.selectedWeapon = 'baby-missile';
        } else {
          tank.inventory[tank.selectedWeapon] = qty - 1;
        }
      }

      s.pendingFire = {
        angle: aimToRadians(currentAim.angle),
        power: aimToPowerFrac(currentAim.power),
      };
      triggerLaunchFx(tank.selectedWeapon);
      sfxLaunch(tank.selectedWeapon);
      currentAim.fired = false;
      pickerInput.open = false;
      // Online: append to action log so the share link reflects this turn.
      if (online.active) {
        const wIdx = WEAPONS.findIndex(w => w.id === tank.selectedWeapon);
        if (wIdx >= 0) {
          recordAction(online, {
            weaponIdx: wIdx,
            angleDeg: Math.round(currentAim.angle),
            power: Math.round(currentAim.power),
          });
          pendingShareForPlayer = cp;
        }
      }
    }
  } else {
    if (currentAim.fired) currentAim.fired = false;
  }

  // Bridge sim events -> render effects
  if (s.lastHit) {
    const palette = getWeaponPalette(s.lastHit.weaponId);
    spawnExplosion(s.lastHit.x, s.lastHit.y, s.lastHit.radius, palette, s.lastHit.weaponId);
    triggerShake(s.lastHit.radius);
    sfxExplosion(s.lastHit.radius, s.lastHit.weaponId);
    s.lastHit = null;
  }

  // Detect tank destruction (alive -> dead) for tank-specific sound + banner
  for (let i = 0; i < s.tanks.length; i++) {
    const wasAlive = aliveLastTick[i] ?? true;
    if (wasAlive && !s.tanks[i].alive) {
      sfxTankDestroyed();
      // Pick snarky quote. Killer = current shooter (may equal victim for self-KO).
      const victim = s.tanks[i].name;
      const killerIdx = s.turn.currentPlayer;
      const killer = s.tanks[killerIdx]?.name ?? victim;
      deathBanner = {
        text: pickDeathQuote(victim, killer),
        until: performance.now() + DEATH_BANNER_MS,
      };
    }
    aliveLastTick[i] = s.tanks[i].alive;
  }
  // Clear banner once it has expired
  if (deathBanner && performance.now() >= deathBanner.until) {
    deathBanner = null;
  }

  // Update particles each frame
  updateParticles(SIM_DT, s.world.gravity);
}

function drawGameFrame(): void {
  const cp = state.turn.currentPlayer;
  drawScene(
    renderer, state, SIM_DT, aimStates[cp], showingTurnBanner, hudMode, pickerState,
    { wins: menu.wins, target: menu.matchTarget },
    deathBanner,
    lastRoundEarnings,
  );
}
